import React, { useState } from 'react';

interface TikTokRecipeFormProps {
  onSubmit: (url: string, targetLanguage?: string) => void;
  loading: boolean;
  backendStatus: 'checking' | 'online' | 'offline';
  onCheckBackend: () => void;
}

export const TikTokRecipeForm: React.FC<TikTokRecipeFormProps> = ({ 
  onSubmit, 
  loading, 
  backendStatus,
  onCheckBackend 
}) => {
  const [url, setUrl] = useState('');
  const [targetLanguage, setTargetLanguage] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url.trim()) {
      alert('Veuillez entrer une URL TikTok');
      return;
    }

    onSubmit(url.trim(), targetLanguage || undefined);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {backendStatus === 'offline' && (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm font-medium text-yellow-800 mb-2">
            ⚠️ Backend non disponible
          </p>
          <p className="text-sm text-yellow-700 mb-3">
            Le serveur backend n'est pas accessible. Assurez-vous qu'il est démarré :
          </p>
          <pre className="bg-yellow-100 p-2 rounded text-xs text-yellow-900 mb-3">
            cd packages/backend{'\n'}
            npm install{'\n'}
            npm run dev
          </pre>
          <button
            type="button"
            onClick={onCheckBackend}
            className="text-sm text-yellow-800 underline hover:text-yellow-900"
          >
            Vérifier à nouveau
          </button>
        </div>
      )}

      {backendStatus === 'online' && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm font-medium text-green-800">
            ✅ Backend connecté - L'extraction automatique est activée !
          </p>
        </div>
      )}

      <div>
        <label htmlFor="tiktok-url" className="block text-sm font-medium text-gray-700 mb-2">
          URL TikTok <span className="text-red-500">*</span>
        </label>
        <input
          id="tiktok-url"
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.tiktok.com/@username/video/... ou https://vm.tiktok.com/..."
          disabled={loading || backendStatus === 'offline'}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        <p className="mt-1 text-sm text-gray-500">
          Collez simplement le lien de la vidéo TikTok
        </p>
      </div>

      <div>
        <label htmlFor="target-language" className="block text-sm font-medium text-gray-700 mb-2">
          🌍 Langue cible (traduction automatique)
        </label>
        <select
          id="target-language"
          value={targetLanguage}
          onChange={(e) => setTargetLanguage(e.target.value)}
          disabled={loading || backendStatus === 'offline'}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">🔤 Garder la langue originale</option>
          <option value="fr">🇫🇷 Français (FR)</option>
          <option value="en">🇬🇧 Anglais (EN)</option>
          <option value="es">🇪🇸 Espagnol (ES)</option>
          <option value="it">🇮🇹 Italien (IT)</option>
          <option value="de">🇩🇪 Allemand (DE)</option>
        </select>
        <p className="mt-1 text-sm text-gray-500">
          Si vous sélectionnez une langue, l'IA traduira automatiquement les ingrédients et les étapes, puis cherchera les correspondances dans la base de données
        </p>
      </div>

      <button 
        type="submit" 
        disabled={loading || backendStatus === 'offline'} 
        className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? '⏳ Extraction en cours...' : '🔍 Extraire la recette automatiquement'}
      </button>

      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">✨ Extraction automatique !</h4>
        <p className="text-sm text-blue-800 mb-2">
          Il vous suffit de coller le lien TikTok, et l'application va automatiquement :
        </p>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>📝 Extraire la description de la vidéo</li>
          <li>🎤 Récupérer la transcription de la vidéo (Transcript24)</li>
          <li>🤖 Analyser avec l'IA Gemini</li>
          <li>📋 Générer la recette structurée</li>
        </ul>
        <div className="mt-3 pt-3 border-t border-blue-200">
          <p className="text-sm font-medium text-blue-900 mb-1">Comment obtenir l'URL ?</p>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>Ouvrez la vidéo TikTok sur tiktok.com</li>
            <li>Cliquez sur "Partager" puis "Copier le lien"</li>
            <li>Collez-le ici !</li>
          </ol>
        </div>
      </div>
    </form>
  );
};


